import React, { useEffect, useRef, useState } from 'react';
import { shuffleSocialVerses, recommendSocialVerses, makeSocialCards, verseTopics, readSocialInterests, rememberSocialRead, SOCIAL_INTEREST_KEY, SOCIAL_TOPICS } from '../utils/socialBible';
import './BibleSocial.css';
import socialImages from '../data/socialImages.json';
import { attachSocialImages } from '../utils/socialBibleImages';
import { mixSocialEditions } from '../utils/mixSocialEditions';
import { loadGlobalSocialPool, interleaveSocialCollections, SOCIAL_COLLECTIONS } from '../utils/globalSocialBible';
import SocialLoading from './SocialLoading';
import { loadSocialCommentary } from '../utils/socialCommentary';

const LOADING_DELAY = 2000;

const EDITIONS = { norm: '口語訳', ruby: '口語訳・ルビ', en: 'English', gal: 'ギャル訳', hebrew: 'ヘブライ語', greek: 'ギリシャ語', latin: 'ラテン語' };
function Icon({ name }) {
  const paths = {
    thread: 'M5 4h14v11H9l-4 4V4Z',
    quote: 'M4 6h6v7H6v4H3v-4l1-7Zm10 0h6v7h-4v4h-3v-4l1-7Z',
    save: 'M6 3h12v18l-6-4-6 4V3Z',
    share: 'M12 15V3m-4 4 4-4 4 4M5 12v8h14v-8',
    refresh: 'M20 7v5h-5M4 17v-5h5M5 8a7 7 0 0 1 12-3l3 3M4 16l3 3a7 7 0 0 0 12-3',
    book: 'M12 5v15M12 5C9 3 5 3 2 4v15c3-1 7-1 10 1 3-2 7-2 10-1V4c-3-1-7-1-10 1Z',
  };
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={paths[name]} /></svg>;
}

function Avatar({ type, collection }) {
  const group = SOCIAL_COLLECTIONS[collection];
  return <div className="bible-social__avatar" aria-hidden="true">
    <img src={`${process.env.PUBLIC_URL}${type === 'gal' ? '/static/images/kirikan2.jpg' : group.icon}`} alt="" width="40" height="40" decoding="async" />
    {type === 'gal' && <img className="bible-social__collection-badge" src={`${process.env.PUBLIC_URL}${group.icon}`} alt="" width="18" height="18" />}
  </div>;
}

function FeedCard({ card, onCommentary, onMessage }) {
  const { book, bookName, collection } = card.item;
  const type = card.type;
  const editionLabel = collection === 'apocrypha' && type === 'norm' ? '通常訳' : EDITIONS[type];
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const item = card.item;
  const [commentary, setCommentary] = useState(null);
  useEffect(() => {
    if (card.kind !== 'quote') return;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    loadSocialCommentary(item, controller.signal).then(result => {
      if (!controller.signal.aborted) setCommentary(result);
    }).catch(() => { /* Keep the Scripture card when commentary is unavailable. */ })
      .finally(() => clearTimeout(timeout));
    return () => { controller.abort(); clearTimeout(timeout); };
  }, [card.kind, item]);
  const kind = card.kind === 'quote' && !commentary ? 'verse' : card.kind;
  useEffect(() => {
    if (!window.IntersectionObserver) return;
    let timer;
    let recorded = false;
    const observer = new IntersectionObserver(([entry]) => {
      clearTimeout(timer);
      if (entry.isIntersecting && !recorded) timer = setTimeout(() => {
        if (document.hidden) return;
        rememberSocialRead(item.text); recorded = true;
      }, 2500);
    }, { threshold: 0.6 });
    observer.observe(ref.current);
    return () => { observer.disconnect(); clearTimeout(timer); };
  }, [item]);
  const href = verse => `/bible/${book}/${verse.chapter}/${verse.verse}?type=${type}`;
  const byline = verse => <div className="bible-social__byline"><strong>{bookName}</strong><span>@{book} · {SOCIAL_COLLECTIONS[collection].label} · {editionLabel}</span><a href={href(verse)}>{verse.reference} ↗</a></div>;
  const text = verse => <p className="bible-social__verse" dir={type === 'hebrew' ? 'rtl' : 'auto'}>{verse.text}</p>;
  const topics = verseTopics(item.text);
  return <article className={`bible-social__card bible-social__card--${kind}`} data-edition={type} data-collection={collection} ref={ref}>
    <div className="bible-social__post">
      <Avatar type={type} collection={collection} />
      <div className="bible-social__post-content">
        {kind === 'quote' ? <>
          <div className="bible-social__byline"><strong>{commentary.author}</strong><span>註解抜粋 · QT · {commentary.language === 'ja' ? '日本語訳（機械翻訳）' : '英語原文'}</span></div>
          <p className="bible-social__verse">{commentary.excerpt}</p>
          <a className="bible-social__quote" href={href(item)}><span><Icon name="book" />{bookName} <span>{item.reference} · {editionLabel}</span></span>{text(item)}</a>
        </> : <>{byline(item)}{text(item)}</>}
        {card.image && !imageFailed && <figure className="bible-social__artwork">
          <a href={`${process.env.PUBLIC_URL}${card.image.src}`} target="_blank" rel="noopener noreferrer" aria-label="挿絵を大きく見る">
            <img src={`${process.env.PUBLIC_URL}${card.image.src}`} width={card.image.width} height={card.image.height}
              alt="ランダムに選ばれたサイトの挿絵" loading="lazy" decoding="async" onError={() => setImageFailed(true)} />
          </a>
        </figure>}
        {topics.length > 0 && <div className="bible-social__tags">{topics.slice(0, 3).map(topic => <span key={topic.id}>#{topic.label}</span>)}</div>}
        <div className="bible-social__actions">
          <a href={href(item)} aria-label={`${item.reference}を本文で読む`}><Icon name="book" /><span>本文</span></a>
          {onCommentary && collection !== 'apocrypha' && ['norm', 'ruby', 'en', 'gal'].includes(type) && <button type="button" className="reformation-verse-button" onClick={() => onCommentary({ book, bookName, chapter: item.chapter, verse: item.verse, verseText: item.text })} aria-label={`${item.reference}の註解を開く`}><Icon name="thread" /><span>註解</span></button>}
          <button type="button" aria-label={`${item.reference}のリンクをコピー`} onClick={async () => {
            try { await navigator.clipboard.writeText(new URL(href(item), window.location.origin).href); onMessage('聖句のリンクをコピーしました。'); }
            catch { onMessage('コピーできませんでした。「本文」から聖句を開けます。'); }
          }}><Icon name="share" /><span>リンク</span></button>
        </div>
      </div>
    </div>
    {card.kind === 'thread' && <>
      {(expanded ? card.verses.slice(1) : card.verses.slice(1, 2)).map(verse => <div className="bible-social__post bible-social__post--reply" key={verse.id}><Avatar type={type} collection={collection} /><div className="bible-social__post-content">{byline(verse)}{text(verse)}</div></div>)}
      {card.verses.length > 2 && <button className="bible-social__thread-more" type="button" aria-expanded={expanded} onClick={() => { setExpanded(!expanded); rememberSocialRead(item.text); }}>{expanded ? 'スレッドをたたむ' : 'このスレッドを読む'} <span aria-hidden="true">{expanded ? '↑' : '↓'}</span></button>}
    </>}
  </article>;
}

export default function BibleSocial({ onCommentary }) {
  const [tab, setTab] = useState('recommended');
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [limit, setLimit] = useState(12);
  const [loadingMore, setLoadingMore] = useState(false);
  const moreTimer = useRef(null);
  const [message, setMessage] = useState('');
  const [loadError, setLoadError] = useState(false);
  const [mixedEditions, setMixedEditions] = useState(true);
  const [interests, setInterests] = useState(readSocialInterests);
  const previous = useRef('');
  const headerRef = useRef(null);
  useEffect(() => {
    const controller = new AbortController();
    let disposed = false;
    let delayTimer;
    const timeout = setTimeout(() => controller.abort(), 15000);
    clearTimeout(moreTimer.current);
    moreTimer.current = null;
    setLoadingMore(false);
    setLoading(true); setLimit(12); setLoadError(false); setMessage('');
    const delay = new Promise(resolve => { delayTimer = setTimeout(resolve, LOADING_DELAY); });
    Promise.all([loadGlobalSocialPool(controller.signal), delay]).then(([pool]) => {
      if (disposed) return;
      clearTimeout(timeout);
      const key = 'bible-social-first:all';
      let last = previous.current;
      try { last = sessionStorage.getItem(key) || last; } catch { /* Session storage is optional. */ }
      const profile = readSocialInterests();
      setInterests(profile);
      const prepare = (source, edition) => {
        const ordered = tab === 'recommended' ? recommendSocialVerses(source, profile, last) : shuffleSocialVerses(source, last);
        const candidates = makeSocialCards(ordered, source).map(card => ({ ...card, type: edition }));
        return interleaveSocialCollections(candidates).slice(0, 120);
      };
      const mixed = mixSocialEditions(prepare(pool.gal, 'gal'), prepare(pool.norm, 'norm'));
      setMixedEditions(pool.gal.length > 0 && pool.norm.length > 0);
      if (mixed[0]?.item.id === last) {
        const alternative = mixed.findIndex((card, index) => index > 0 && index < 10 && card.item.id !== last);
        if (alternative > 0) [mixed[0], mixed[alternative]] = [mixed[alternative], mixed[0]];
      }
      previous.current = mixed[0]?.item.id || '';
      try { sessionStorage.setItem(key, previous.current); } catch { /* Session storage is optional. */ }
      setCards(attachSocialImages(mixed, socialImages)); setLoading(false);
      if (pool.partial) setMessage('一部の本文を読み込めなかったため、読み込めた書巻から表示しています。');
    }).catch(() => {
      clearTimeout(timeout);
      clearTimeout(delayTimer);
      if (!disposed) { setLoading(false); setLoadError(true); setCards([]); }
    });
    return () => {
      disposed = true;
      controller.abort();
      clearTimeout(timeout);
      clearTimeout(delayTimer);
      clearTimeout(moreTimer.current);
      moreTimer.current = null;
    };
  }, [refresh, tab]);
  const loadMore = () => {
    if (loading || moreTimer.current !== null) return;
    setLoadingMore(true);
    moreTimer.current = setTimeout(() => {
      setLimit(value => value + 12);
      setLoadingMore(false);
      moreTimer.current = null;
    }, LOADING_DELAY);
  };
  const words = SOCIAL_TOPICS.filter(topic => interests[topic.id] > 0).sort((a, b) => interests[b.id] - interests[a.id]).slice(0, 3);

  return <div className="bible-social">
    <header className="bible-social__header" ref={headerRef}><div><span className="bible-social__eyebrow">SCRIPTURE / TIMELINE</span><h1>ことばに、出会う。</h1><p>旧約・新約・外典<span> · {mixedEditions ? 'ギャル訳 60% / 通常訳 40%' : '全書巻からピックアップ'}</span></p></div><span className="bible-social__mark" aria-hidden="true">✦</span></header>
    <div className="bible-social__intro">スクロールする日常に、ひとつの聖句を。</div>
    <nav className="bible-social__tabs" aria-label="SNSの読み方">{[['recommended', 'おすすめ'], ['random', 'ランダム']].map(([id, label]) => <button type="button" aria-pressed={tab === id} key={id} onClick={() => setTab(id)}>{label}</button>)}</nav>
    <details className="bible-social__preferences"><summary>{words.length ? `気になることば：${words.map(word => word.label).join('・')}` : 'あなたの読む時間から、次の出会いへ'}</summary><p>このSNS画面でしばらく読んだ聖句の単語を、この端末に保存しておすすめに使います。他サイトの履歴は使いません。QTは、その聖句に対応する註解の抜粋です。</p><button type="button" onClick={() => {
      try { localStorage.removeItem(SOCIAL_INTEREST_KEY); setInterests({}); setMessage('おすすめの履歴をリセットしました。'); setRefresh(value => value + 1); }
      catch { setMessage('履歴をリセットできませんでした。'); }
    }}>おすすめ履歴をリセット</button></details>
    <div className="bible-social__feed-heading"><span>聖句、スレッド、ときどきQT。</span><button type="button" disabled={loading} onClick={() => setRefresh(value => value + 1)}><Icon name="refresh" />新しいことば</button></div>
    <div className="bible-social__status" role="status">{message}</div>
    {loading ? <div aria-busy="true" className="bible-social__loading"><SocialLoading label="ことばを探しています…" /></div> : <>
      {!cards.length && <p className="bible-social__empty">{loadError ? '聖句を読み込めませんでした。' : '表示できる聖句がありません。'}<button type="button" onClick={() => setRefresh(value => value + 1)}>再読み込み</button></p>}
      {cards.slice(0, limit).map(card => <FeedCard key={`${refresh}-${tab}-${card.type}-${card.item.id}`} card={card} onCommentary={onCommentary} onMessage={setMessage} />)}
      {cards.length > limit ? <div aria-busy={loadingMore} className="bible-social__pagination">{loadingMore ? <SocialLoading label="続きを読み込んでいます…" /> : <button className="bible-social__more" type="button" onClick={loadMore}>続きを読み込む ↓</button>}</div> : cards.length > 0 && <div className="bible-social__end"><p>新しい書巻のことばに、出会う。</p><button type="button" onClick={() => { setRefresh(value => value + 1); headerRef.current?.scrollIntoView({ block: 'start' }); }}>もう一度出会う ↻</button></div>}
    </>}
  </div>;
}
